import axios from "axios";
import { captureError, saveLogRocketSession, getStoredToken, clearSentryUser } from "../utils";

const AUTH_ENDPOINTS = [
    '/authenticate', '/loginAuthenticate', '/loginWithHash',
    '/register', '/registerVerifyCode', '/sendForgotEmail',
    '/checkValidHash', '/checkValidEmailHash',
    '/adminAuthenticate', '/adminloginAuthenticate', '/adminRegisterAuthenticate',
    '/adminSentCode', '/adminUserByHash', '/adminVerificationCode',
    '/updatePassword', '/sentCode', '/send-auth-text',
    '/verifyLoginAuthenticate', '/verifyCodeWithHash',
    '/adminverifyLoginAuthenticate',
];

let isRedirectingFor401 = false;

function isAuthEndpoint(url) {
    if (!url) return false;
    return AUTH_ENDPOINTS.some(ep => url.includes(ep));
}

// SECURITY: Helper function to get CSRF token from cookie
function getCsrfTokenFromCookie() {
    const name = 'XSRF-TOKEN=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookies = decodedCookie.split(';');
    for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.indexOf(name) === 0) {
            return cookie.substring(name.length);
        }
    }
    return null;
}

// SECURITY: Configure axios to send credentials (cookies) with requests
axios.defaults.withCredentials = true;
axios.defaults.timeout = 30000;

axios.interceptors.request.use(
    config => {
        if (config.headers['micro-service'] === 'appointment') {
            config.baseURL = 'https://new-api.sosoftpay.com/api/';
            // config.baseURL = 'http://localhost:5001/api/';
            config.headers['Content-Type'] = 'application/json';

            return config;
        }
        config.baseURL = process.env.REACT_APP_API;
        const token = getStoredToken();
        if (token) {
            config.headers.Authorization = `bearer ${token}`;
        }

        // SECURITY: Include CSRF token in header for state-changing requests
        const csrfToken = getCsrfTokenFromCookie();
        if (csrfToken && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase())) {
            config.headers['X-CSRF-Token'] = csrfToken;
        }

        config.headers['Content-Type'] = 'application/json';
        return config;
    },
    error => Promise.reject(error)
);

axios.interceptors.response.use(
    response => response,
    error => {
        const status = error.response?.status;
        const requestUrl = error.config?.url || '';

        // Handle 401: expired/invalid JWT — skip for auth endpoints where 401 means wrong credentials
        if (status === 401 && !isAuthEndpoint(requestUrl)) {
            saveLogRocketSession({
                eventType: "auth_expired",
                metadata: { url: requestUrl, method: error.config?.method },
            });
            if (!isRedirectingFor401) {
                isRedirectingFor401 = true;
                localStorage.removeItem("token");
                localStorage.removeItem("user");
                localStorage.removeItem("payerUser");
                try { clearSentryUser(); } catch (e) {}
                setTimeout(() => {
                    isRedirectingFor401 = false;
                    window.location.reload();
                }, 100);
            }
            return Promise.reject(error);
        }

        const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
            || /timeout/i.test(error.message);
        const isPaymentEndpoint = /nmiTxn|token|payments/i.test(requestUrl);
        captureError(error, {
            errorType: isTimeout ? 'timeout' : 'api_error',
            context: {
                url: requestUrl,
                method: error.config?.method,
                status: status,
                data: error.response?.data,
                ...(isPaymentEndpoint && { paymentRelated: true }),
                ...(isTimeout && {
                    errorCode: error.code,
                    timeoutMs: error.config?.timeout,
                }),
            },
        });
        saveLogRocketSession({
            eventType: isTimeout && isPaymentEndpoint ? "payment_timeout" : "api_error",
            metadata: {
                url: requestUrl,
                method: error.config?.method,
                status: status,
                ...(isTimeout && { errorCode: error.code }),
            },
        });
        return Promise.reject(error);
    }
);

export default axios;


// const axiosApi = axios.create({
//   baseURL: baseURL,
// });

// export const setAuthHeader = (token) => {
//   axiosApi.defaults.headers.common.Authorization = `bearer ${
//     token || localStorage.getItem("token")
//   }`;
// };

// axiosApi.defaults.headers.common.Authorization = `bearer ${localStorage.getItem(
//   "token"
// )}`;

// //if a 401 happens, the user will be logged out
// axiosApi.interceptors.response.use(
//   function (response) {
//     return response;
//   },
//   function (error) {
//     if (error && error.response && error.response.status === 401) {
//       localStorage.removeItem("token");
//       localStorage.removeItem("user");
//     }
//     return Promise.reject(error);
//   }
// );

// // Set the initial header from storage or something (should surround with try catch in actual app)
// setAuthHeader(localStorage.getItem("token"));

// export default axiosApi;
